import { CheckoutAccountFormStatus, InvestmentTrigger, InvestmentTriggerQuery, SignupStep } from 'types/checkout';
import { enumToKebab } from '@filters/shared-filters';
import { InvestmentEntityType } from 'types/investment-entity';
import { RouteLocationRaw } from 'vue-router';
import { signupStepToRouteMap } from '@constants/checkout';
import { useSignupStore } from '@stores/signup';

export function mapInvestmentTrigger(trigger: InvestmentTriggerQuery): InvestmentTrigger {
	switch (trigger) {
		case 'browse-investments':
			return 'Browse Investments';
		case 'calculator':
			return 'Calculator';
		case 'featured-module':
			return 'Featured module';
		case 'fund-detail':
			return 'Fund Detail';
		case 'goal':
			return 'Goal';
		case 'growth-checklist':
			return 'Growth Account Checklist';
		case 'invest-tab-fund':
			return 'Invest Tab fund';
		case 'invest-tab-plan':
			return 'Invest Tab plan';
		case 'ira-navigation':
			return 'IRA navigation';
		case 'milestone-module':
			return 'Milestone module';
		case 'portfolio-module':
			return 'Portfolio module';
		case 'portfolio':
			return 'Portfolio';
		case 'post-signup':
			return 'Post Signup X-sell';
		case 'settings-accounts':
			return 'Settings - Accounts';
		case 'signup':
			return 'Signup';
		case 'spotlight':
			return 'Spotlight';
		case 'takeover-modal':
			return 'Full Screen Takeover';
	}
}

export function mapInvestmentTriggerToRoute(trigger?: InvestmentTrigger): string {
	switch (trigger) {
		case 'Browse Investments':
			return 'offerings';
		case 'iPO Landing page':
			return 'offerings-ipo-landing';
		case 'Fund Detail':
			return 'view-offering-details';
		case 'Goal':
		case 'Calculator':
			return 'growth-calculator';
		case 'Featured module':
		case 'Milestone module':
		case 'Spotlight':
		case 'Full Screen Takeover':
		case 'Growth Account Checklist':
		case 'Post Signup X-sell':
			return 'account-overview';
		case 'Invest Tab fund':
		case 'Invest Tab plan':
		default:
			return 'account-invest';
	}
}

export async function getNextRoute(currentStep?: SignupStep): Promise<RouteLocationRaw> {
	const signupStore = useSignupStore();
	switch (currentStep?.signupStepType) {
		case 'INFO':
			if (currentStep.infoName) {
				const infoSlug = enumToKebab(currentStep.infoName);
				return { name: 'info-screen', params: { infoSlug } };
			} else {
				return { name: 'info-framework' };
			}
		case 'QUESTION':
			if (currentStep.questionName) {
				const questionId = enumToKebab(currentStep.questionName);
				return { name: 'question', params: { questionId } };
			} else {
				return { name: 'question-framework' };
			}
		case 'CUSTOM':
		default: {
			let route: RouteLocationRaw;

			if (currentStep?.signupStepName) {
				route = { name: signupStepToRouteMap[currentStep.signupStepName] };
			} else {
				route = await signupStore.goToNextStep();
			}

			return route;
		}
	}
}

export const accountDetailsRoute = async (
	entityType: InvestmentEntityType,
	userRoute: CheckoutAccountFormStatus
): Promise<RouteLocationRaw> => {
	const signupStore = useSignupStore();
	switch (entityType) {
		case 'INDIVIDUAL':
			await signupStore.getUserCheckoutDetailsData('CONTACT_INFORMATION_STEP');
			return { name: 'checkout-individual-account-details' };
		case 'JOINT_ACCOUNT':
			await signupStore.getUserCheckoutDetailsData('JOINT_INFORMATION_STEP');
			return { name: 'checkout-joint-account-details', query: { account: userRoute } };
		case 'LLC':
			await signupStore.getUserCheckoutDetailsData('ENTITY_INFORMATION_STEP');
			return {
				name: 'checkout-entity-account-details',
				query: { account: userRoute }
			};
		case 'TRUST':
			await signupStore.getUserCheckoutDetailsData('TRUST_INFORMATION_STEP');
			return { name: 'checkout-trust-account-details', query: { account: userRoute } };
		default:
			return { name: 'checkout-resume' };
	}
};
